import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, PackageOpen } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ProductCard } from "../components/ProductCard";
import { CategoryIllustration } from "../components/CategoryIllustration";
import { WhatsappButton } from "../components/WhatsappButton";
import { useSite } from "../context/SiteContext";
import { api } from "../lib/api";

export default function CategoryPage() {
  const { slug } = useParams();
  const { categories } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = categories.find((c) => c.slug === slug);
  const subs = category?.subcategories || [];
  const segments = category?.segments || [];
  const activeSub = searchParams.get("sub") || "";
  const activeSeg = searchParams.get("seg") || "";

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products?category=${encodeURIComponent(slug)}`)
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [slug]);

  const visibleProducts = products.filter((p) => {
    if (activeSub && p.subcategory !== activeSub) return false;
    if (activeSeg && p.segment !== activeSeg) return false;
    return true;
  });

  const setSub = (sub) => {
    const next = {};
    if (sub) next.sub = sub;
    if (activeSeg) next.seg = activeSeg;
    setSearchParams(next);
  };

  const setSeg = (seg) => {
    const next = {};
    if (activeSub) next.sub = activeSub;
    if (seg) next.seg = seg;
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Service hero */}
      <section className="relative overflow-hidden border-b border-line bg-surface">
        {category?.image_url && (
          <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url(${category.image_url})` }} />
        )}
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-navy-soft hover:text-brand-dark transition-colors mb-6" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </Link>
          <div className="flex items-center gap-4">
            <CategoryIllustration icon={category?.icon} className="w-16 h-16 sm:w-20 sm:h-20" />
            <div>
              <div className="label-eyebrow text-brand-dark mb-1">Servizio</div>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-navy">
                {category?.name || "Servizio"}
              </h1>
            </div>
          </div>
          {category?.description && (
            <p className="mt-5 text-lg text-navy-soft max-w-2xl leading-relaxed">{category.description}</p>
          )}

          {/* Subcategory filter chips (Tipo) */}
          {subs.length > 0 && (
            <div className="mt-7 flex flex-wrap gap-2" data-testid="subcategory-filters">
              <button
                onClick={() => setSub("")}
                data-testid="subfilter-all"
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  !activeSub ? "bg-brand text-white soft-shadow" : "bg-white border border-line text-navy hover:border-brand"
                }`}
              >
                Tutte
              </button>
              {subs.map((sub) => (
                <button
                  key={sub}
                  onClick={() => setSub(sub)}
                  data-testid={`subfilter-${sub}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    activeSub === sub ? "bg-brand text-white soft-shadow" : "bg-white border border-line text-navy hover:border-brand"
                  }`}
                >
                  {sub}
                </button>
              ))}
            </div>
          )}

          {/* Segment filter chips (Privato / Business) */}
          {segments.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="segment-filters">
              <span className="text-xs font-bold uppercase tracking-wide text-navy-soft mr-1">Per:</span>
              <button
                onClick={() => setSeg("")}
                data-testid="segfilter-all"
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  !activeSeg ? "bg-navy text-white" : "bg-white border border-line text-navy hover:border-navy"
                }`}
              >
                Tutti
              </button>
              {segments.map((seg) => (
                <button
                  key={seg}
                  onClick={() => setSeg(seg)}
                  data-testid={`segfilter-${seg}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                    activeSeg === seg ? "bg-navy text-white" : "bg-white border border-line text-navy hover:border-navy"
                  }`}
                >
                  {seg}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Products */}
      <section className="max-w-7xl mx-auto w-full px-5 sm:px-8 py-16 flex-1">
        {loading ? (
          <div className="text-navy-soft py-20 text-center">Caricamento offerte…</div>
        ) : visibleProducts.length === 0 ? (
          <div className="bg-surface rounded-2xl border border-line py-20 text-center" data-testid="no-products">
            <PackageOpen className="w-10 h-10 text-navy-soft mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-navy">Offerte in arrivo</h3>
            <p className="mt-2 text-navy-soft text-sm">Contattaci su WhatsApp per conoscere le proposte attive.</p>
            <div className="mt-6 flex justify-center">
              <WhatsappButton product={`offerte ${category?.name || ""}`} category={category?.name} label="Chiedi le offerte" />
            </div>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-grid">
            {visibleProducts.map((p) => (
              <ProductCard key={p.id} product={p} categoryName={category?.name || slug} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
